/* Conversion steps the store's own checkout events do not cover (CONVERSION-AUDIT.md, decision 4).
   Each is published through Shopify's customer-events API (Shopify.analytics.publish), so a custom
   pixel in the admin (Settings > Customer events) can forward it to GA4 and Google Ads, and Shopify
   applies the visitor's cookie choice before any pixel sees it. The theme sends nothing to Google
   itself. The pixel's code lives in TRACKING.md on project-docs.

     st_quote_started       first answer in a quote quiz, once per page view
     st_quote_sent          a quote form sent (read from the success message the server renders)
     st_contact_sent        the contact form sent (same)
     st_pack_changed        the count on a product page's pack picker or the homepage buy box,
                            once settled (below)
     st_sticky_add_to_cart  Add to cart pressed on the mobile sticky bar
     st_quote_priced_add    Add to cart pressed on the quote quiz's priced ending (published by
                            assets/st-quiz-price.js)

   "Sent" is read from the page Shopify renders after a successful post, never from the click, so a
   blocked or invalid submit is not counted (hCaptcha cancels the click; see assets/st-motion.js).
   It counts only when this tab submitted a contact form in the last hour: submitting leaves a
   marker in sessionStorage, the success page publishes once and removes it, and contact_posted
   is taken out of the address. Until 2026-10-16 any visit to ?contact_posted=true counted (a
   reload, back and forward, a shared link), and each one reached GA4 and Google Ads as a new lead
   (code review).

   st_pack_changed (Matt, 2026-10-16: Meta's ViewContent follows the chosen quantity and value;
   TRACKING-QA.md). The pickers price each count themselves (assets/st-pack.js, assets/st-buy.js: the
   cheapest mix of packs; Horizon's variant is never switched) and announce it as an st:pack-mix
   event, so nothing is worked out twice. It publishes 1 second after the last change, and only when
   the product, count or mix differs from the last one sent (the count on show at page load counts as
   sent, since the page view already carries it), so typing 23 or holding + publishes once:
     product_id, variant_ids (the packs in the mix), count, value (the mix total), currency (the
     buyer's), contents ([{ id, quantity, item_price }], one per pack line), pack (the mix as text,
     for the GA4 select_pack event) and event_id, for Meta's deduplication.
   Values are in the currency's units (22.94), as in assets/st-funnel.js; ids are strings.
   Each settled change also goes to Meta as ViewContent through Upstack (assets/st-upstack.js),
   since Upstack's Customer events pixel forwards only Shopify's own events. Until
   2026-10-16 it sent only { pack } from an <output> the picker no longer renders, so pack was empty,
   and the homepage buy box sent nothing. */
(() => {
  const publish = (name, data = {}) => {
    try {
      window.Shopify?.analytics?.publish?.(name, { page: location.pathname, ...data });
    } catch (err) {}
  };
  const eventId = () => (window.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const PENDING = 'st-contact-submitted';
  const HOUR = 60 * 60 * 1000;
  // A contact or quote form leaving this tab (the quiz and the contact page both post form_type=contact).
  // Marked on the submit itself and on the click that starts it, captured on window: hCaptcha cancels
  // the click and posts afterwards, which may fire no submit event at all.
  const mark = (form) => {
    if (!(form instanceof HTMLFormElement) || form.querySelector('input[name="form_type"]')?.value !== 'contact') return;
    try {
      sessionStorage.setItem(PENDING, String(Date.now()));
    } catch (err) {}
  };
  window.addEventListener('submit', (event) => mark(event.target), true);
  window.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[type="submit"], input[type="submit"], button:not([type])') : null;
      if (button) mark(button.form);
    },
    true
  );

  const sent = () => {
    const done = document.querySelectorAll('[data-st-track]');
    if (!done.length) return;
    let pending = 0;
    try {
      pending = Number(sessionStorage.getItem(PENDING)) || 0;
      sessionStorage.removeItem(PENDING);
    } catch (err) {}
    if (pending && Date.now() - pending < HOUR) {
      done.forEach((el) => publish(el.dataset.stTrack, el.dataset.stForm ? { form: el.dataset.stForm } : {}));
    }
    // The success message stays on screen; a reload or a copied address shows the form again.
    const url = new URL(location.href);
    if (url.searchParams.has('contact_posted')) {
      url.searchParams.delete('contact_posted');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sent);
  else sent();

  const started = new WeakSet();
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const quiz = target.closest('.st-quiz__card');
    if (quiz && !started.has(quiz)) {
      started.add(quiz);
      const form = quiz.querySelector('input[name="contact[Form]"]');
      publish('st_quote_started', form ? { form: form.value } : {});
    }
  });

  const SETTLE = 1000;
  const packs = new WeakMap(); // picker -> { timer, last: the key last sent }
  document.addEventListener('st:pack-mix', (event) => {
    const el = event.target;
    const d = event.detail;
    if (!(el instanceof Element) || !d?.items?.length) return;
    const key = `${d.product}|${d.count}|${d.items.map((it) => `${it.id}:${it.quantity}`).join(',')}`;
    const state = packs.get(el) || {};
    packs.set(el, state);
    clearTimeout(state.timer);
    if (d.initial) {
      state.last = key;
      return;
    }
    state.timer = setTimeout(() => {
      // A picker swapped away meanwhile (assets/st-platform-swap.js publishes the new product).
      if (key === state.last || !el.isConnected) return;
      state.last = key;
      publish('st_pack_changed', {
        product_id: String(d.product),
        variant_ids: d.items.map((it) => String(it.id)),
        count: d.count,
        value: Math.round(d.total) / 100,
        currency: d.currency,
        contents: d.items.map((it) => ({ id: String(it.id), quantity: it.quantity, item_price: Math.round(it.price) / 100 })),
        pack: d.items.map((it) => `${it.quantity} × ${it.label}`).join(' + '),
        event_id: eventId(),
      });
      // Meta's ViewContent at the settled value, through Upstack (assets/st-upstack.js).
      window.stUpstackView?.({
        productId: d.product,
        title: d.title,
        currency: d.currency,
        value: Math.round(d.total) / 100,
        items: d.items.map((it) => ({ variant: it.id, quantity: it.quantity, price: Math.round(it.price) / 100, label: it.label })),
      });
    }, SETTLE);
  });

  document.addEventListener('st:sticky-buy', (event) => publish('st_sticky_add_to_cart', { pack: event.detail?.pack || '' }));
})();
